import { gql } from '@apollo/client';
import { List } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';
import { Redirect, Route } from 'react-router';

import * as ApolloTypes from 'src/lib/graphqlTypes/types';
import {
  NormalizedMetricsField,
  fromGQLOperationsListItemToOperationsListItem,
} from 'src/lib/metrics';
import { mergeQueryParams } from 'src/lib/routing';

import { OperationsListItem } from '../insightsListItem/OperationsListItem';

type OperationsListProps = {
  serviceId: string;
  data: ApolloTypes.UI__OperationsListQuery;
  selectedQueryId?: string;
  selectedQueryName?: string;
  statsSortKey: NormalizedMetricsField;
  loading: boolean;
};

// Previously in LegacyOperationsListBody and currently used in metrics.ts aggregateStats()
export const QueryListItemMetricsFragment = gql`
  fragment QueryListItemMetricsFragment on ServiceQueryStatsMetrics {
    uncachedRequestsCount
    cachedRequestsCount
    cacheTtlHistogram {
      serviceTimeP50: durationMs(percentile: 0.5)
    }
    totalLatencyHistogram {
      totalCount
      totalDurationMs
      serviceTimeP50: durationMs(percentile: 0.5)
      serviceTimeP90: durationMs(percentile: 0.9)
      serviceTimeP95: durationMs(percentile: 0.95)
      serviceTimeP99: durationMs(percentile: 0.99)
    }
    requestsWithErrorsCount
    registeredOperationCount
  }
`;

// Previously in LegacyOperationsListBody and currently used in metrics.ts aggregateStats()
export const QueryListItemMetricsGroupFragment = gql`
  fragment QueryListItemMetricsGroupFragment on ServiceQueryStatsDimensions {
    id: queryId
    signature: querySignature
    name: queryName
  }
`;

const OperationsListBody = ({
  data,
  selectedQueryId,
  selectedQueryName,
  statsSortKey,
  loading,
}: OperationsListProps) => {
  const operations =
    data.graph?.variant?.operationInsightsList?.operations ?? [];

  const selectedQuery =
    selectedQueryId && operations.find((query) => query.id === selectedQueryId);
  // Redirect to url with query name if available data is found.
  if (selectedQuery && !selectedQueryName && selectedQuery.name) {
    return (
      <Route>
        {({ location }) => (
          <Redirect
            to={{
              ...location,
              search: mergeQueryParams(location.search, {
                queryName: selectedQuery.name,
              }),
            }}
          />
        )}
      </Route>
    );
  }

  const operationsListItems = operations.map((stats) => {
    return {
      ...stats,
      ...fromGQLOperationsListItemToOperationsListItem(stats),
      statsSortKey,
      isSelected: stats.id === selectedQueryId,
    };
  });

  const opsListContent = operationsListItems.map((item) => (
    <OperationsListItem key={item.id} {...item} loading={loading} />
  ));

  return (
    <div
      className={classnames('p-0 py-2', {
        'motion-safe:animate-pulse-faded': loading,
      })}
    >
      <List isInteractive>{opsListContent}</List>
    </div>
  );
};

export { OperationsListBody };
